import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Typography } from "antd";
import { ArrowLeftOutlined, FileExcelOutlined } from "@ant-design/icons";
import { SbTable, SbTag, SbButton, SbTooltip } from "uiCore";
import {
  PageContainerStyled,
  PageHeaderStyled,
  HeaderLeftStyled,
  HeaderRightStyled,
  HeadingRowStyled,
} from "styles/common.styles";
import { PageTransitionStyled } from "../talentMobility.animations";
import { BackLinkStyled } from "../RoleDetailV2.styles";
import {
  InfoCardStyled,
  InfoStatStyled,
  ReductionTableWrapperStyled,
} from "./RedeploymentReductionDetailV2.styles";
import { useRoleReductionDetail, fetchNextRolesForPersonId } from "../useTalentMobilityData";
import useRedeploymentPlanStore, {
  useAvailableEmployeeIdsForRole,
  useShortlistReasonsForRole,
} from "Store/useRedeploymentPlanStore";
import useRedeploymentPlanActions from "../RedeploymentCenterV2/useRedeploymentPlanActions";
import PlanSaveButton from "../components/PlanSaveButton";
import { exportRowsAsCsv } from "Utils/exportCsv.utils";
import { useReductionDetailColumns } from "./useReductionDetailColumns";
import { IndexInfoLabel } from "../components/IndexTooltip";
import {
  buildReductionCsvRows,
  computeReductionCutoff,
  orderReductionOccupants,
  parseReductionTarget,
} from "./RedeploymentReductionDetailV2.utils";
import AtRiskDetailPanel from "./components/AtRiskDetailPanel";
import ShortlistConfirmModal from "../components/ShortlistConfirmModal";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Title, Text } = Typography;

const ANALYTICS_FROM = "talent_mobility_reduction_detail";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.REDUCTION_DETAIL_VIEWED;

const RedeploymentReductionDetailV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const target = parseReductionTarget(searchParams.get("target"));
  // API roles carry their title in the link from Role View — the
  // jd-active-employees endpoint returns occupants only, no JD metadata.
  const { role, occupants, isLoading } = useRoleReductionDetail(roleId, searchParams.get("title"));
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  // The dashed cutoff line under the last retained row only means something
  // in the default full ranking — a sorter or filter reorders/hides rows, so
  // the line is dropped while one is active (Rank and Status stay correct).
  const [isTableReordered, setIsTableReordered] = useState(false);
  // Next roles fetched for the CSV export, kept per person so a re-export
  // (or an export after a failed one) doesn't refetch what already resolved.
  const exportNextRolesCacheRef = useRef(new Map());
  // "Already available?" is answered by this role's redeployment plan
  // (keyed by current_role_jd_sb_unique_id), not the person-level flag —
  // an employee marked available for a different role's reduction stays "No".
  const availableEmployeeIds = useAvailableEmployeeIdsForRole(roleId);
  const shortlistReasons = useShortlistReasonsForRole(roleId);
  const updateShortlistDetails = useRedeploymentPlanStore((state) => state.updateShortlistDetails);
  // The Reason cell's Edit action — one dialog for the whole page, not one
  // per row. The person is kept separately from the open flag so the
  // dialog's text doesn't blank out during its close animation.
  const [reasonEditPerson, setReasonEditPerson] = useState(null);
  const [isReasonEditOpen, setIsReasonEditOpen] = useState(false);
  const reasonEditEntry = useRedeploymentPlanStore((state) =>
    state.availableEmployeeTargets[roleId]?.find((entry) => entry.employee_unique_id === reasonEditPerson?.id)
  );
  // Stable reference, so opening the dialog doesn't rebuild the columns.
  const handleEditReason = useCallback((record) => {
    setReasonEditPerson(record.person);
    setIsReasonEditOpen(true);
  }, []);
  const setTargetPosition = useRedeploymentPlanStore((state) => state.setTargetPosition);
  // Save & Close lands back on the Redeployment Center, where the saved
  // plan's effects are visible.
  const { hasDraft, isSubmitting, planName, planLabel, isUpdate, savePlan } = useRedeploymentPlanActions({
    onClosed: () => navigate("/talent-mobility/redeployment-center"),
  });

  // Entering with an explicit ?target= (from Role View, or a shared link)
  // records it as this role's plan target_position, so the plan payload is
  // complete even when the Role View editor was never touched this session.
  useEffect(() => {
    const targetFromUrl = parseReductionTarget(searchParams.get("target"));
    if (Number.isFinite(targetFromUrl) && targetFromUrl >= 0) setTargetPosition(roleId, targetFromUrl);
  }, [roleId, searchParams, setTargetPosition]);

  // Default ranking: SMI only — shortlisting never reorders rows. Rank,
  // Status, the cutoff line, expandability and the CSV all derive from
  // positions in this ordering. Shared with the Candidate Profile page (see
  // RedeploymentReductionDetailV2.utils) so a profile opened from here
  // reproduces the same ranking/cutoff.
  const orderedOccupants = useMemo(() => orderReductionOccupants(occupants), [occupants]);

  const cutoff = computeReductionCutoff(role, target);
  const columns = useReductionDetailColumns({
    cutoff,
    availableEmployeeIds,
    shortlistReasons,
    onEditReason: handleEditReason,
    occupants: orderedOccupants,
    currentRoleJdId: roleId,
  });

  if (!role) {
    return (
      <PageContainerStyled>
        <PageHeaderStyled>
          <HeaderLeftStyled>
            <Title level={4}>Role not found</Title>
          </HeaderLeftStyled>
        </PageHeaderStyled>
      </PageContainerStyled>
    );
  }

  const atRiskCount = Math.max(orderedOccupants.length - cutoff, 0);

  const toggleRow = (personId) =>
    setExpandedRowKeys((prev) => (prev.includes(personId) ? prev.filter((k) => k !== personId) : [...prev, personId]));

  // API occupants arrive with nextRoles: null (loaded lazily per expanded
  // row), so the export fetches the missing ones itself — otherwise "Top
  // Suggested Next Role(s)" would be "—" for every row never expanded. A
  // person whose fetch fails exports as "—" rather than blocking the file.
  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const cache = exportNextRolesCacheRef.current;
      const enrichedOccupants = await Promise.all(
        orderedOccupants.map(async (occupant) => {
          if (occupant.person?.nextRoles) return occupant;
          const personId = occupant.person?.id;
          if (!cache.has(personId)) {
            cache.set(
              personId,
              fetchNextRolesForPersonId(personId).catch(() => {
                cache.delete(personId);
                return null;
              })
            );
          }
          const nextRoles = await cache.get(personId);
          return { ...occupant, person: { ...occupant.person, nextRoles } };
        })
      );
      const rows = buildReductionCsvRows({
        roleTitle: role.title,
        occupants: enrichedOccupants,
        cutoff,
        availableEmployeeIds,
        shortlistReasons,
      });
      exportRowsAsCsv(rows, `redeployment-reduction-${role.id}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageTransitionStyled>
      <PageHeaderStyled>
        <HeaderLeftStyled>
          <BackLinkStyled onClick={() => navigate("/talent-mobility/redeployment-center")}>
            <ArrowLeftOutlined /> Back to Redeployment Center
          </BackLinkStyled>
          <HeadingRowStyled>
            <Title level={4}>{role.title} — Reduction Detail</Title>
            <SbTag color={isUpdate ? "blue" : "gold"} style={{ fontSize: 14 }}>{planLabel}</SbTag>
          </HeadingRowStyled>
          <Text type="secondary">
            Everyone currently in this role, ranked by <IndexInfoLabel index="SMI" />. Everyone below the target headcount is at risk in this reduction.
          </Text>
        </HeaderLeftStyled>
        <HeaderRightStyled>
          <SbButton
            buttonType="secondary"
            icon={<FileExcelOutlined />}
            loading={isExporting}
            disabled={isLoading}
            onClick={handleExportCsv}
          >
            Export Redeployment Analysis CSV
          </SbButton>
          <PlanSaveButton
            planTypeLabel="Redeployment Plan"
            disabled={isLoading}
            hasDraft={hasDraft}
            isUpdate={isUpdate}
            planName={planName}
            isSubmitting={isSubmitting}
            onSave={savePlan}
          />
          <PlanSaveButton
            planTypeLabel="Redeployment Plan"
            buttonType="secondary"
            closeAfter
            disabled={isLoading}
            hasDraft={hasDraft}
            isUpdate={isUpdate}
            planName={planName}
            isSubmitting={isSubmitting}
            onSave={savePlan}
          />
        </HeaderRightStyled>
      </PageHeaderStyled>

      <InfoCardStyled>
        <InfoStatStyled>
          <span className="label">Current Positions</span>
          <span className="value">{role.currentPositionCount}</span>
        </InfoStatStyled>
        <InfoStatStyled>
          <span className="label">Target Positions</span>
          <span className="value">{cutoff}</span>
        </InfoStatStyled>
        <InfoStatStyled>
          <span className="label">At Risk</span>
          <span className="value">
            <SbTooltip content="People ranked below the target headcount for this role.">
              <SbTag color={atRiskCount > 0 ? "error" : "success"} style={{ fontSize: 14 }}>{atRiskCount}</SbTag>
            </SbTooltip>
          </span>
        </InfoStatStyled>
      </InfoCardStyled>

      <ReductionTableWrapperStyled>
        <SbTable
          columns={columns}
          dataSource={orderedOccupants}
          loading={isLoading}
          rowKey={(record) => record.person.id}
          pagination={false}
          sticky
          rowClassName={(record) => {
            if (isTableReordered || record.person.id !== orderedOccupants[cutoff - 1]?.person?.id) return "";
            // Expanded, the cutoff line moves below its next-roles panel so
            // the panel stays on the Retained side of the line.
            return expandedRowKeys.includes(record.person.id) ? "cutoff-row-expanded" : "cutoff-row";
          }}
          onChange={(_, filters, sorter) => {
            const sorterActive = [].concat(sorter).some((s) => s?.order);
            const filtersActive = Object.values(filters || {}).some((value) => value?.length);
            setIsTableReordered(sorterActive || filtersActive);
          }}
          onRow={(record) => ({
            onClick: () => toggleRow(record.person.id),
          })}
          expandable={{
            expandedRowKeys,
            showExpandColumn: false,
            // Every occupant expands to their next roles — Retained people can be
            // shortlisted for redeployment too (see the row-level shortlist column).
            rowExpandable: () => true,
            expandedRowRender: (record) => (
              <AtRiskDetailPanel
                person={record.person}
                currentRoleJdId={role.id}
                currentRoleTitle={role.title}
                currentRoleTarget={cutoff}
              />
            ),
          }}
        />
      </ReductionTableWrapperStyled>

      <ShortlistConfirmModal
        mode="edit"
        open={isReasonEditOpen}
        personName={reasonEditPerson?.name}
        initialValues={{
          notifications: reasonEditEntry?.notifications,
          reason: reasonEditEntry?.shortlist_reason,
        }}
        onCancel={() => setIsReasonEditOpen(false)}
        onConfirm={({ notifications, reason }) => {
          updateShortlistDetails(roleId, reasonEditPerson.id, { notifications, reason });
          setIsReasonEditOpen(false);
        }}
      />
    </PageTransitionStyled>
  );
};

export default RedeploymentReductionDetailV2;
